import type {
  ClimbAssignment,
  RouteSettingBoardCard,
  RouteSettingSetter,
  RouteSettingZone,
} from '../../../types/route-setting-board';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_WEEK = 7;

/** Column id used for the climbs that have no value for the current grouping. */
export const UNASSIGNED_COLUMN_ID = '__unassigned__';

/** What the columns stand for. Only zones can be dragged between. */
export type BoardGrouping = 'zone' | 'setter' | 'grade';

export const BOARD_GROUPINGS: BoardGrouping[] = ['zone', 'setter', 'grade'];

/** Zones are the board's original reading, and stay the one that can be edited. */
export const DEFAULT_GROUPING: BoardGrouping = 'zone';

export interface BoardFilters {
  /** Case-insensitive substring of the climb's name; empty means "every name". */
  name: string;
  /** [min, max] whole days since the climb was set. */
  ageRange: [number, number];
  /** Empty means "every setter", so a fresh board shows everything. */
  setterIds: string[];
  /** Grade labels (e.g. "6a"), empty means "every grade". */
  grades: string[];
  /** Hold colour names as the gym writes them; empty means "every colour". */
  holdColors: string[];
}

/** How the cards are ordered inside every column. */
export type BoardSort = 'newest' | 'oldest' | 'best' | 'worst';

export const BOARD_SORTS: BoardSort[] = ['newest', 'oldest', 'best', 'worst'];

/** A fresh reset reads from the top, which is what the board is watched for. */
export const DEFAULT_SORT: BoardSort = 'newest';

export interface BoardColumn {
  /** Stable drop-target id: the zone id, the setter id, the grade — or UNASSIGNED_COLUMN_ID. */
  id: string;
  /** null for the "no value" column — the caller supplies its label. */
  label: string | null;
  /** Which grouping produced the column; the header needs it for that label. */
  grouping: BoardGrouping;
  cards: RouteSettingBoardCard[];
  /** Mean age in days of the cards in the column; null when empty. */
  averageAgeDays: number | null;
  /** Sends across every card in the column. */
  sendsCount: number;
}

export interface BoardSummary {
  total: number;
  averageAgeDays: number | null;
  averageRating: number | null;
  sendsCount: number;
  /** Climbs set per week over the span these cards cover; null when empty. */
  climbsPerWeek: number | null;
}

/** Whole days between a climb's creation and now, floored at 0. */
export function daysSince(iso: string, now: number = Date.now()): number {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return 0;

  return Math.max(0, Math.floor((now - created) / MS_PER_DAY));
}

function mean(values: number[]): number | null {
  if (values.length === 0) return null;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** The widest age range present, used to bound the filter slider. */
export function ageBounds(
  cards: RouteSettingBoardCard[],
  now: number = Date.now()
): [number, number] {
  if (cards.length === 0) return [0, 0];

  const ages = cards.map((card) => daysSince(card.created_at, now));
  return [Math.min(...ages), Math.max(...ages)];
}

/** The setters with at least one climb on the board, by name. */
export function settersOf(cards: RouteSettingBoardCard[]): RouteSettingSetter[] {
  const byId = new Map<string, RouteSettingSetter>();
  cards.forEach((card) => {
    if (card.route_setter) byId.set(card.route_setter.id, card.route_setter);
  });

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** The hold colours in use on the board, with a swatch when a scan found one. */
export function holdColorsOf(
  cards: RouteSettingBoardCard[]
): Array<{ name: string; hex: string | null }> {
  const byName = new Map<string, string | null>();
  cards.forEach((card) => {
    if (!card.hold_color) return;
    // First swatch wins, but a later card that has one fills in for an earlier
    // card that did not.
    const hex = card.hold_color_hex?.[0] ?? null;
    if (!byName.has(card.hold_color) || (hex && !byName.get(card.hold_color))) {
      byName.set(card.hold_color, hex);
    }
  });

  return [...byName.entries()]
    .map(([name, hex]) => ({ name, hex }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The grades present on the board, easiest first. */
export function gradesOf(cards: RouteSettingBoardCard[]): string[] {
  const values = new Map<string, number>();
  cards.forEach((card) => {
    if (card.difficulty) {
      values.set(card.difficulty, card.difficulty_value ?? Number.MAX_SAFE_INTEGER);
    }
  });

  return [...values.entries()]
    .sort(([nameA, valueA], [nameB, valueB]) => valueA - valueB || nameA.localeCompare(nameB))
    .map(([name]) => name);
}

export function filterCards(
  cards: RouteSettingBoardCard[],
  filters: BoardFilters,
  now: number = Date.now()
): RouteSettingBoardCard[] {
  const [minAge, maxAge] = filters.ageRange;
  const name = filters.name.trim().toLowerCase();

  return cards.filter((card) => {
    if (name.length > 0 && !card.name.toLowerCase().includes(name)) return false;

    const age = daysSince(card.created_at, now);
    if (age < minAge || age > maxAge) return false;

    if (filters.setterIds.length > 0) {
      if (!card.route_setter || !filters.setterIds.includes(card.route_setter.id)) return false;
    }

    if (filters.grades.length > 0) {
      if (!card.difficulty || !filters.grades.includes(card.difficulty)) return false;
    }

    if (filters.holdColors.length > 0) {
      if (!card.hold_color || !filters.holdColors.includes(card.hold_color)) return false;
    }

    return true;
  });
}

/**
 * A climb nobody has rated has no rating, which is not the same as a bad one.
 * Read as null so both rating sorts can push it past the rated climbs instead of
 * letting an unrated wall top the "worst" list.
 */
function ratingOf(card: RouteSettingBoardCard): number | null {
  return card.average_rating !== null && card.average_rating > 0 ? card.average_rating : null;
}

/** Orders the cards inside a column. Ties fall back to newest first. */
export function sortCards(
  cards: RouteSettingBoardCard[],
  sort: BoardSort = DEFAULT_SORT,
  now: number = Date.now()
): RouteSettingBoardCard[] {
  const newestFirst = (a: RouteSettingBoardCard, b: RouteSettingBoardCard) =>
    daysSince(a.created_at, now) - daysSince(b.created_at, now);

  const byRating = (a: RouteSettingBoardCard, b: RouteSettingBoardCard, best: boolean) => {
    const [ratingA, ratingB] = [ratingOf(a), ratingOf(b)];
    if (ratingA === null && ratingB === null) return newestFirst(a, b);
    if (ratingA === null) return 1;
    if (ratingB === null) return -1;
    if (ratingA === ratingB) return newestFirst(a, b);

    return best ? ratingB - ratingA : ratingA - ratingB;
  };

  const sorted = [...cards];

  switch (sort) {
    case 'oldest':
      return sorted.sort((a, b) => newestFirst(b, a));
    case 'best':
      return sorted.sort((a, b) => byRating(a, b, true));
    case 'worst':
      return sorted.sort((a, b) => byRating(a, b, false));
    default:
      return sorted.sort(newestFirst);
  }
}

/**
 * Cuts the cards into columns the chosen way. Zones are the board's own reading
 * and the only one you can drag between; setter and grade are read-only views of
 * the same climbs, for the two other questions a head setter asks — who set what,
 * and whether the spread of grades still covers the gym.
 */
export function buildColumns(
  cards: RouteSettingBoardCard[],
  zones: RouteSettingZone[],
  grouping: BoardGrouping = DEFAULT_GROUPING,
  now: number = Date.now(),
  sort: BoardSort = DEFAULT_SORT
): BoardColumn[] {
  switch (grouping) {
    case 'setter':
      return setterColumns(cards, now, sort);
    case 'grade':
      return gradeColumns(cards, now, sort);
    default:
      return zoneColumns(cards, zones, now, sort);
  }
}

/**
 * Ordered left to right by descending average age: the wall that has gone
 * longest without a reset comes first, which is the whole point of the board.
 *
 * A zone holding nothing gets no column — same rule as every other grouping. It
 * does mean an emptied zone drops off the board until something is put back in
 * it another way, which is the price of a board with no dead columns on it.
 */
function zoneColumns(
  cards: RouteSettingBoardCard[],
  zones: RouteSettingZone[],
  now: number,
  sort: BoardSort
): BoardColumn[] {
  const buckets = groupBy(cards, (card) => card.zone_id ?? UNASSIGNED_COLUMN_ID);

  const columns = zones
    .filter((zone) => buckets.has(zone.id))
    .map((zone) => toColumn(zone.id, zone.name, 'zone', buckets.get(zone.id) ?? [], now, sort));

  const unassigned = buckets.get(UNASSIGNED_COLUMN_ID) ?? [];
  if (unassigned.length > 0) {
    columns.push(toColumn(UNASSIGNED_COLUMN_ID, null, 'zone', unassigned, now, sort));
  }

  return columns.sort(byDescendingAge);
}

/**
 * One column per setter with a climb on the board — the payload never mentions
 * the ones who have nothing up. Ordered like the zones, oldest average first:
 * whose climbs have been up longest is the same question asked of people instead
 * of walls.
 */
function setterColumns(
  cards: RouteSettingBoardCard[],
  now: number,
  sort: BoardSort
): BoardColumn[] {
  const buckets = groupBy(cards, (card) => card.route_setter?.id ?? UNASSIGNED_COLUMN_ID);

  return [...buckets.entries()]
    .map(([id, bucket]) => {
      const setter = bucket[0].route_setter;

      return toColumn(
        id,
        setter?.name ?? null,
        'setter',
        bucket,
        now,
        sort
      );
    })
    .sort(byDescendingAge);
}

/**
 * One column per grade in play, easiest first. A scale has an order of its own,
 * so this is the one grouping that ignores age: sorted by anything else the
 * columns would read as a scrambled ladder. Ungraded climbs go last.
 *
 * Keyed by the grade itself rather than by its label, so a drop names a grade
 * the API can assign — and two scales that both spell a grade "6a" stay apart.
 */
function gradeColumns(
  cards: RouteSettingBoardCard[],
  now: number,
  sort: BoardSort
): BoardColumn[] {
  const buckets = groupBy(cards, (card) => card.grade_id ?? UNASSIGNED_COLUMN_ID);

  return [...buckets.entries()]
    .map(([id, bucket]) => toColumn(id, bucket[0].difficulty, 'grade', bucket, now, sort))
    .sort((a, b) => gradeValueOf(a) - gradeValueOf(b));
}

function gradeValueOf(column: BoardColumn): number {
  return column.cards[0]?.difficulty_value ?? Number.MAX_SAFE_INTEGER;
}

function groupBy(
  cards: RouteSettingBoardCard[],
  key: (card: RouteSettingBoardCard) => string
): Map<string, RouteSettingBoardCard[]> {
  const buckets = new Map<string, RouteSettingBoardCard[]>();
  cards.forEach((card) => {
    const bucket = buckets.get(key(card));
    if (bucket) bucket.push(card);
    else buckets.set(key(card), [card]);
  });

  return buckets;
}

/** Oldest average first; the columns with nothing in them fall to the end. */
function byDescendingAge(a: BoardColumn, b: BoardColumn): number {
  if (a.averageAgeDays === null && b.averageAgeDays === null) return 0;
  if (a.averageAgeDays === null) return 1;
  if (b.averageAgeDays === null) return -1;

  return b.averageAgeDays - a.averageAgeDays;
}

function toColumn(
  id: string,
  label: string | null,
  grouping: BoardGrouping,
  bucket: RouteSettingBoardCard[],
  now: number,
  sort: BoardSort
): BoardColumn {
  const cards = sortCards(bucket, sort, now);

  return {
    id,
    label,
    grouping,
    cards,
    averageAgeDays: mean(cards.map((card) => daysSince(card.created_at, now))),
    sendsCount: cards.reduce((sum, card) => sum + card.sends_count, 0),
  };
}

/** Which column a card currently sits in, under the given grouping. */
export function columnIdOf(card: RouteSettingBoardCard, grouping: BoardGrouping): string {
  switch (grouping) {
    case 'setter':
      return card.route_setter?.id ?? UNASSIGNED_COLUMN_ID;
    case 'grade':
      return card.grade_id ?? UNASSIGNED_COLUMN_ID;
    default:
      return card.zone_id ?? UNASSIGNED_COLUMN_ID;
  }
}

export interface ColumnAssignment {
  /** The one attribute the drop assigns. */
  payload: ClimbAssignment;
  /** What to show on the card while the request is in flight. */
  patch: Partial<RouteSettingBoardCard>;
  /**
   * Whether to ask before doing it. Sorting a climb into a zone is housekeeping,
   * undone by dragging it back; recrediting it to someone else or regrading it
   * changes the climb itself, and a slipped finger should not.
   */
  confirms: boolean;
}

/**
 * What dropping a card on `column` means. The new value is read off a card
 * already in the column, which is always there because empty columns are not
 * drawn.
 */
export function columnAssignment(column: BoardColumn): ColumnAssignment {
  const target = column.id === UNASSIGNED_COLUMN_ID ? null : column.id;
  const sample = column.cards[0];

  switch (column.grouping) {
    case 'setter':
      return {
        payload: { route_setter_id: target },
        patch: { route_setter: target ? sample?.route_setter ?? null : null },
        confirms: true,
      };
    case 'grade':
      return {
        payload: { grade_id: target },
        patch: {
          grade_id: target,
          difficulty: sample?.difficulty ?? null,
          difficulty_value: sample?.difficulty_value ?? null,
        },
        confirms: true,
      };
    default:
      return { payload: { zone_id: target }, patch: { zone_id: target }, confirms: false };
  }
}

/**
 * How a climb's age reads against the board's own average. The bands are
 * symmetrical around it: 80% past the average has waited too long, 80% short of
 * it is still fresh.
 */
export type AgeStatus = 'fresh' | 'normal' | 'due' | 'overdue';

const FRESH_SHARE = 0.2;
const OVERDUE_SHARE = 1.8;

export function ageStatus(days: number, averageDays: number | null): AgeStatus {
  // Nothing to compare against on an empty board, and a board where everything
  // went up today would otherwise paint itself entirely one colour.
  if (averageDays === null || averageDays <= 0) return 'normal';

  if (days < averageDays * FRESH_SHARE) return 'fresh';
  if (days > averageDays * OVERDUE_SHARE) return 'overdue';
  if (days > averageDays) return 'due';

  return 'normal';
}

/**
 * The pace the wall is being set at: the cards divided by the weeks they span,
 * counting from the day the oldest of them went up through today.
 *
 * It reads off the board rather than off the whole history on purpose — a climb
 * that has been taken down is no longer work the wall is carrying, so the number
 * answers "how fast is this wall turning over right now", which is the question
 * a head setter plans the next session from.
 *
 * The span is floored at one week, so a board that was entirely reset in the
 * last few days reads as "this many this week" instead of dividing by a fraction
 * of a week and inventing a rate nobody set at.
 */
export function climbsPerWeek(
  cards: RouteSettingBoardCard[],
  now: number = Date.now()
): number | null {
  if (cards.length === 0) return null;

  const oldest = Math.max(...cards.map((card) => daysSince(card.created_at, now)));
  // +1: a climb set today spans today, not zero days.
  const weeks = Math.max(1, (oldest + 1) / DAYS_PER_WEEK);

  return cards.length / weeks;
}

/** The headline numbers above the board, over the currently filtered cards. */
export function summarize(
  cards: RouteSettingBoardCard[],
  now: number = Date.now()
): BoardSummary {
  const ratings = cards
    .map((card) => card.average_rating)
    .filter((rating): rating is number => rating !== null && rating > 0);

  return {
    total: cards.length,
    averageAgeDays: mean(cards.map((card) => daysSince(card.created_at, now))),
    averageRating: mean(ratings),
    sendsCount: cards.reduce((sum, card) => sum + card.sends_count, 0),
    climbsPerWeek: climbsPerWeek(cards, now),
  };
}
